import React, { useState, useEffect } from 'react';
import { X, Building2, Shield, User, Network, Check, ChevronRight, ChevronLeft, AlertCircle, Loader2 } from 'lucide-react';
import api from '../../api';
import toast from 'react-hot-toast';

interface NewTenantWizardProps {
  isOpen: boolean;
  onClose: () => void;
}

const STEPS = [
  { id: 1, label: 'Create Tenant Profile', icon: Building2 },
  { id: 2, label: 'Configure SSO / MFA',   icon: Shield },
  { id: 3, label: 'Define Admin User',      icon: User },
  { id: 4, label: 'Set IP Whitelist',       icon: Network },
];

const STORAGE_KEY = 'wizard_new_tenant';

interface SavedState {
  step: number;
  completed: number[];
  tenantName: string;
  subdomain: string;
  subscription: 'trial' | 'professional' | 'enterprise';
  ssoProvider: 'none' | 'okta' | 'saml';
  mfaRequired: boolean;
  passwordMinLength: number;
  adminName: string;
  adminEmail: string;
  ipRules: string;
  ipScope: 'all' | 'admin' | 'ops';
}

function loadSaved(): SavedState | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

const NewTenantWizard: React.FC<NewTenantWizardProps> = ({ isOpen, onClose }) => {
  // loadSaved() is called once at component mount via the lazy initialiser arrow function
  const [step, setStep] = useState<number>(() => loadSaved()?.step ?? 1);
  const [completed, setCompleted] = useState<Set<number>>(() => new Set(loadSaved()?.completed ?? []));
  const [submitting, setSubmitting] = useState(false);

  // Step 1
  const [tenantName, setTenantName] = useState<string>(() => loadSaved()?.tenantName ?? '');
  const [subdomain, setSubdomain] = useState<string>(() => loadSaved()?.subdomain ?? '');
  const [subscription, setSubscription] = useState<'trial' | 'professional' | 'enterprise'>(() => loadSaved()?.subscription ?? 'trial');

  // Step 2
  const [ssoProvider, setSsoProvider] = useState<'none' | 'okta' | 'saml'>(() => loadSaved()?.ssoProvider ?? 'none');
  const [mfaRequired, setMfaRequired] = useState<boolean>(() => loadSaved()?.mfaRequired ?? true);
  const [passwordMinLength, setPasswordMinLength] = useState<number>(() => loadSaved()?.passwordMinLength ?? 12);

  // Step 3 — password never persisted (security)
  const [adminName, setAdminName] = useState<string>(() => loadSaved()?.adminName ?? '');
  const [adminEmail, setAdminEmail] = useState<string>(() => loadSaved()?.adminEmail ?? '');
  const [adminPassword, setAdminPassword] = useState('');

  // Step 4
  const [ipRules, setIpRules] = useState<string>(() => loadSaved()?.ipRules ?? '');
  const [ipScope, setIpScope] = useState<'all' | 'admin' | 'ops'>(() => loadSaved()?.ipScope ?? 'admin');

  // Persist state to localStorage on every change (except password)
  useEffect(() => {
    if (!isOpen) return;
    const state: SavedState = {
      step,
      completed: Array.from(completed),
      tenantName, subdomain, subscription,
      ssoProvider, mfaRequired, passwordMinLength,
      adminName, adminEmail,
      ipRules, ipScope,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }, [step, completed, tenantName, subdomain, subscription, ssoProvider, mfaRequired, passwordMinLength, adminName, adminEmail, ipRules, ipScope, isOpen]);

  if (!isOpen) return null;

  const canAdvance = () => {
    if (step === 1) return tenantName.trim().length > 0 && subdomain.trim().length > 0;
    if (step === 2) return true;
    if (step === 3) return adminName.trim().length > 0 && adminEmail.includes('@') && adminPassword.length >= 8;
    return true;
  };

  const advance = () => {
    if (!canAdvance()) return;
    setCompleted(prev => new Set(prev).add(step));
    if (step < 4) setStep(step + 1);
  };

  const finish = async () => {
    setSubmitting(true);
    try {
      await api.post('/api/admin/wizards/tenant', {
        tenantName, subdomain, subscription,
        ssoProvider, mfaRequired, passwordMinLength,
        adminName, adminEmail, adminPassword,
        ipRules, ipScope,
      });
      setCompleted(prev => new Set(prev).add(4));
      toast.success(`Tenant "${tenantName}" activated successfully`);
      localStorage.removeItem(STORAGE_KEY); // Clear saved state on success
      onClose();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed to provision tenant');
    } finally {
      setSubmitting(false);
    }
  };

  const stepValid = (s: number) => completed.has(s);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-white font-bold text-lg">New Tenant Onboarding</h2>
            <p className="text-blue-200 text-xs mt-0.5">Walk through all four steps to fully onboard a new client tenant. The tenant remains inactive until Step 4 is complete and saved.</p>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Step progress */}
        <div className="px-6 pt-5 pb-3">
          <div className="flex items-center gap-0">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              const isActive = step === s.id;
              const isDone = stepValid(s.id);
              return (
                <React.Fragment key={s.id}>
                  <button
                    onClick={() => (isDone || isActive) ? setStep(s.id) : undefined}
                    className={`flex flex-col items-center min-w-0 flex-1 ${isDone ? 'cursor-pointer' : 'cursor-default'}`}
                  >
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center border-2 transition-all ${
                      isDone ? 'bg-blue-600 border-blue-600 text-white' :
                      isActive ? 'bg-white border-blue-600 text-blue-600' :
                      'bg-gray-100 border-gray-200 text-gray-400'
                    }`}>
                      {isDone ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                    </div>
                    <span className={`text-[10px] font-medium mt-1 text-center leading-tight ${isActive ? 'text-blue-700' : isDone ? 'text-blue-500' : 'text-gray-400'}`}>
                      {s.label}
                    </span>
                  </button>
                  {i < STEPS.length - 1 && (
                    <div className={`h-0.5 flex-1 mx-1 rounded transition-all ${stepValid(s.id) ? 'bg-blue-500' : 'bg-gray-200'}`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Step content */}
        <div className="px-6 py-4 min-h-[260px]">
          {/* Step 1 — Tenant Profile */}
          {step === 1 && (
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-900 text-base flex items-center gap-2"><Building2 className="w-4 h-4 text-blue-600" />Create Tenant Profile</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Tenant Name <span className="text-red-500">*</span></label>
                  <input value={tenantName} onChange={e => setTenantName(e.target.value)} placeholder="Acme Financial" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Subdomain <span className="text-red-500">*</span></label>
                  <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-blue-500">
                    <input value={subdomain} onChange={e => setSubdomain(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))} placeholder="acme" className="flex-1 px-3 py-2 text-sm outline-none" />
                    <span className="px-2 py-2 bg-gray-50 text-gray-400 text-xs border-l whitespace-nowrap">.railway.app</span>
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Subscription Type</label>
                <div className="flex gap-2">
                  {(['trial', 'professional', 'enterprise'] as const).map(t => (
                    <button key={t} onClick={() => setSubscription(t)}
                      className={`flex-1 py-2 rounded-lg border text-sm font-medium capitalize transition-all ${subscription === t ? 'bg-blue-600 border-blue-600 text-white' : 'border-gray-200 text-gray-600 hover:border-blue-300'}`}>
                      {t}
                    </button>
                  ))}
                </div>
              </div>
              {(!tenantName || !subdomain) && (
                <p className="text-xs text-amber-600 flex items-center gap-1"><AlertCircle className="w-3 h-3" />Tenant name and subdomain are required to proceed.</p>
              )}
            </div>
          )}

          {/* Step 2 — SSO / MFA */}
          {step === 2 && (
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-900 text-base flex items-center gap-2"><Shield className="w-4 h-4 text-blue-600" />Configure SSO / MFA</h3>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">SSO Provider</label>
                <div className="flex gap-2">
                  {([{v:'none',l:'None (Local Auth)'},{v:'okta',l:'Okta'},{v:'saml',l:'Generic SAML 2.0'}] as const).map(({v, l}) => (
                    <button key={v} onClick={() => setSsoProvider(v)}
                      className={`flex-1 py-2 rounded-lg border text-sm font-medium transition-all ${ssoProvider === v ? 'bg-blue-600 border-blue-600 text-white' : 'border-gray-200 text-gray-600 hover:border-blue-300'}`}>
                      {l}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                <div>
                  <p className="text-sm font-medium text-gray-900">Require MFA (TOTP)</p>
                  <p className="text-xs text-gray-500">Enforced for Admin and Super Admin roles</p>
                </div>
                <button onClick={() => setMfaRequired(!mfaRequired)}
                  className={`relative w-10 h-6 rounded-full transition-colors ${mfaRequired ? 'bg-blue-600' : 'bg-gray-300'}`}>
                  <span className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-all ${mfaRequired ? 'left-5' : 'left-1'}`} />
                </button>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Minimum Password Length</label>
                <div className="flex items-center gap-3">
                  <input type="range" min={8} max={24} value={passwordMinLength} onChange={e => setPasswordMinLength(+e.target.value)} className="flex-1" />
                  <span className="text-sm font-bold text-blue-600 w-8 text-right">{passwordMinLength}</span>
                </div>
              </div>
            </div>
          )}

          {/* Step 3 — Admin User */}
          {step === 3 && (
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-900 text-base flex items-center gap-2"><User className="w-4 h-4 text-blue-600" />Define Admin User</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Full Name <span className="text-red-500">*</span></label>
                  <input value={adminName} onChange={e => setAdminName(e.target.value)} placeholder="Jane Smith" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Email <span className="text-red-500">*</span></label>
                  <input type="email" value={adminEmail} onChange={e => setAdminEmail(e.target.value)} placeholder="jane@acme.com" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Temporary Password <span className="text-red-500">*</span></label>
                <input type="password" value={adminPassword} onChange={e => setAdminPassword(e.target.value)} placeholder="Min 8 characters" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
                <p className="text-xs text-gray-400 mt-1">User will be forced to change this on first login. Password is never saved to browser storage.</p>
              </div>
              {(!adminName || !adminEmail.includes('@') || adminPassword.length < 8) && (
                <p className="text-xs text-amber-600 flex items-center gap-1"><AlertCircle className="w-3 h-3" />All fields required. Password must be at least 8 characters.</p>
              )}
            </div>
          )}

          {/* Step 4 — IP Whitelist */}
          {step === 4 && (
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-900 text-base flex items-center gap-2"><Network className="w-4 h-4 text-blue-600" />Set IP Whitelist</h3>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Whitelist Scope</label>
                <div className="flex gap-2">
                  {([{v:'all',l:'All Routes'},{v:'admin',l:'Admin Routes'},{v:'ops',l:'System Operations'}] as const).map(({v, l}) => (
                    <button key={v} onClick={() => setIpScope(v)}
                      className={`flex-1 py-2 rounded-lg border text-sm font-medium transition-all ${ipScope === v ? 'bg-blue-600 border-blue-600 text-white' : 'border-gray-200 text-gray-600 hover:border-blue-300'}`}>
                      {l}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">IP Rules (CIDR notation, one per line)</label>
                <textarea value={ipRules} onChange={e => setIpRules(e.target.value)} rows={4}
                  placeholder={"0.0.0.0/0\n192.168.1.0/24"}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-blue-500 outline-none resize-none" />
                <p className="text-xs text-gray-400 mt-1">Leave blank to allow all IPs. Super Admins always bypass IP restrictions.</p>
              </div>
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-xs text-green-800 font-medium flex items-center gap-1.5"><Check className="w-3.5 h-3.5" />Completing this step activates the tenant.</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
          <button onClick={() => step > 1 && setStep(step - 1)} disabled={step === 1}
            className="flex items-center gap-1.5 px-4 py-2 text-sm text-gray-600 hover:text-gray-900 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
            <ChevronLeft className="w-4 h-4" />Back
          </button>
          <div className="flex items-center gap-1.5">
            {STEPS.map(s => (
              <div key={s.id} className={`w-2 h-2 rounded-full transition-all ${step === s.id ? 'bg-blue-600 w-4' : stepValid(s.id) ? 'bg-blue-300' : 'bg-gray-200'}`} />
            ))}
          </div>
          {step < 4 ? (
            <button onClick={advance} disabled={!canAdvance()}
              className="flex items-center gap-1.5 px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
              Next<ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button onClick={finish} disabled={submitting || !STEPS.slice(0, -1).every(s => stepValid(s.id))}
              className="flex items-center gap-1.5 px-5 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
              {submitting ? <><Loader2 className="w-4 h-4 animate-spin" />Activating…</> : <><Check className="w-4 h-4" />Activate Tenant</>}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default NewTenantWizard;
